import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { AlertIcon, LockIcon, ShieldIcon } from '../common/SvgIcons';
import './DeviceConflictNotification.css';

const DeviceConflictNotification = () => {
  const { deviceConflictError, clearDeviceConflictError, logout } = useAuth();
  const { t } = useLanguage();

  if (!deviceConflictError) return null;

  // Handle both string and object error formats
  const errorMessage = typeof deviceConflictError === 'string' 
    ? deviceConflictError 
    : deviceConflictError.message;
  
  const errorCode = typeof deviceConflictError === 'object' 
    ? deviceConflictError.code 
    : null;
    
  const conflictInfo = typeof deviceConflictError === 'object' 
    ? deviceConflictError.conflictInfo 
    : null;

  const getErrorInfo = () => {
    if (errorCode === 'DEVICE_CONFLICT' || errorMessage?.includes('Another device is currently logged in')) {
      return {
        icon: <AlertIcon className="w-6 h-6 text-rose-500 inline-block mr-2" />,
        title: t('deviceConflict.blockedTitle', 'Device Login Blocked'),
        message: t('deviceConflict.blockedMessage', 'Only one device can be logged in at a time for your security.'),
        details: conflictInfo ? (
          <div className="conflict-details">
            <p><strong>{t('deviceConflict.activeDevice', 'Active Device:')}</strong> {conflictInfo.existingDevice || 'Unknown'}</p>
            <p><strong>{t('deviceConflict.ipAddress', 'IP Address:')}</strong> {conflictInfo.existingIP || 'Unknown'}</p>
            <p><strong>{t('deviceConflict.loginTime', 'Login Time:')}</strong> {conflictInfo.loginTime ? new Date(conflictInfo.loginTime).toLocaleString() : 'Unknown'}</p>
          </div>
        ) : null,
        showLogout: true
      };
    }
    
    if (errorCode === 'SESSION_TERMINATED' || errorMessage?.includes('session has been terminated')) {
      return {
        icon: <LockIcon className="w-6 h-6 text-amber-500 inline-block mr-2" />,
        title: t('deviceConflict.terminatedTitle', 'Session Terminated'),
        message: t('deviceConflict.terminatedMessage', 'Your session was ended because another device logged in.'),
        showLogout: false
      };
    }
    
    if (errorCode === 'DEVICE_MISMATCH' || errorMessage?.includes('Device or location mismatch')) {
      return {
        icon: <ShieldIcon className="w-6 h-6 text-blue-500 inline-block mr-2" />,
        title: t('deviceConflict.securityTitle', 'Security Alert'),
        message: t('deviceConflict.securityMessage', 'Your session was terminated due to a security check (device or location change).'),
        showLogout: false
      };
    }
    
    // Default
    return {
      icon: <AlertIcon className="w-6 h-6 text-amber-500 inline-block mr-2" />,
      title: t('deviceConflict.invalidTitle', 'Session Invalid'),
      message: errorMessage || t('deviceConflict.invalidTitle', 'Your session is no longer valid.'),
      showLogout: true
    };
  };

  const errorInfo = getErrorInfo();

  const handleLoginAgain = () => {
    clearDeviceConflictError();
    window.location.href = '/login';
  };

  const handleLogout = async () => {
    clearDeviceConflictError();
    await logout();
    window.location.href = '/login';
  };

  return (
    <div className="device-conflict-overlay">
      <div className="device-conflict-modal">
        <div className="device-conflict-header">
          <h3>{errorInfo.icon} {errorInfo.title}</h3>
        </div>
        
        <div className="device-conflict-body">
          <p><strong>{errorInfo.message}</strong></p>
          
          {errorInfo.details && (
            <div className="device-conflict-details-section">
              <h4>{t('deviceConflict.activeDevice', 'Current Active Session:')}</h4>
              {errorInfo.details}
            </div>
          )}
          
          <p>{t('deviceConflict.blockedMessage', 'For security reasons, only one active session is allowed per account.')}</p>
        </div>
        
        <div className="device-conflict-actions">
          <button 
            className="btn btn-primary"
            onClick={handleLoginAgain}
          >
            {errorInfo.showLogout ? t('deviceConflict.forceLogin', 'Force Login Here') : t('deviceConflict.loginAgain', 'Login Again')}
          </button>
          
          {errorInfo.showLogout && (
            <button 
              className="btn btn-secondary"
              onClick={handleLogout}
            >
              {t('deviceConflict.logout', 'Logout Completely')}
            </button>
          )}
        </div>
        
        <div className="device-conflict-info">
          <small>
            <strong>{t('deviceConflict.securityNote', 'Security Note: This feature protects your account from unauthorized access.')}</strong>
          </small>
        </div>
      </div>
    </div>
  );
};

export default DeviceConflictNotification;